import { ReactElement, useState } from "react";
import {
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Divider,
} from "@mui/material";
import MethodInfo from "./FormInfo";

export interface CellTypeAnnotationFormProps {
  onClose?: () => void;
  onSubmit?: (data: CellTypeAnnotationData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface CellTypeAnnotationData {
  organism: string;
  tissue: string;
  model: string;
  markers_per_cluster: number;
  user_markers: string;
  additionalParams?: Record<string, any>;
}

const CellTypeAnnotationForm = ({
  onClose,
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: CellTypeAnnotationFormProps): ReactElement => {
  const [formData, setFormData] = useState<CellTypeAnnotationData>({
    organism: "human",
    tissue: "",
    model: "survom-basic",
    markers_per_cluster: 7,
    user_markers: "",
  });
  const [formError, setFormError] = useState("");

  const handleInputChange =
    (field: keyof CellTypeAnnotationData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any,
    ) => {
      const value = event.target.value;
      setFormError("");
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.tissue.trim()) {
      setFormError("Enter the tissue or biological system before annotation.");
      return;
    }
    onSubmit?.(formData);
  };

  return (
    <Box className={`flex flex-col h-full ${className}`}>
      <Box className="flex-1 p-6">
        <MethodInfo
          title="Cell annotation"
          message="Annotation uses cluster marker genes together with the selected organism and tissue. Optional known marker genes may be comma-separated or entered one per line; up to 10 detected user markers are added per cluster. SurvOm Basic is the default annotation mode."
        />

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <TextField
            fullWidth
            label="Organism"
            value={formData.organism}
            onChange={handleInputChange("organism")}
            placeholder="e.g., mouse, human, rat"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
          />

          <TextField
            fullWidth
            label="Tissue"
            value={formData.tissue}
            onChange={handleInputChange("tissue")}
            placeholder="e.g., pancreas, brain, liver"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
          />

          <FormControl fullWidth>
            <InputLabel>Markers per cluster</InputLabel>
            <Select
              value={formData.markers_per_cluster}
              onChange={handleInputChange("markers_per_cluster")}
              label="Markers per cluster"
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value={5}>5 — fastest</MenuItem>
              <MenuItem value={7}>7 — recommended</MenuItem>
              <MenuItem value={10}>10 — detailed</MenuItem>
              <MenuItem value={20}>20 — more detailed</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={3}
            label="Known marker genes (optional)"
            value={formData.user_markers}
            onChange={handleInputChange("user_markers")}
            placeholder="CD3D, MS4A1, NKG7, LST1"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
          />

          <FormControl fullWidth>
            <InputLabel>AI Model</InputLabel>
            <Select
              value={formData.model}
              onChange={handleInputChange("model")}
              label="AI Model"
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="" disabled>Select annotation mode</MenuItem>
              <MenuItem value="survom-basic">SurvOm Basic</MenuItem>
              <MenuItem value="survom-advanced">SurvOm Advanced</MenuItem>
            </Select>
          </FormControl>

          {formError && <Typography color="error" variant="body2">{formError}</Typography>}

          <Divider className="my-4" />
        </form>
      </Box>

      <Box className="flex justify-between items-center p-6 border-t">
        <Button variant="outlined" color="primary" onClick={onBack}>
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default CellTypeAnnotationForm;
